<template>
  <BaseDialog
    :title="$t('editReservationStatusDialog.title')"
    :close-on-click-modal="false"
    @close="$emit('close')"
    @confirm="updateOrder"
    @cancel="$emit('close')"
  >
    <BaseElForm>
      <!-- 訂單狀態 -->
      <BaseElFormItem :label="$t('editReservationStatusDialog.status.title')" prop="status">
        <BaseElSelect
          v-model="formData.status"
          :disabled="isCancel"
          :placeholder="$t('editReservationStatusDialog.status.placeholder')"
        >
          <BaseElSelectOption
            v-for="item in ['wait', 'complete', 'cancel', 'absence', 'confirm']"
            :key="item"
            :label="statusTranslate(item)"
            :value="item"
            :disabled="item === 'confirm'"
          />
        </BaseElSelect>
      </BaseElFormItem>

      <!-- 訂單回覆狀態 -->
      <BaseElFormItem
        v-if="showReplyStatusField"
        :label="$t('reservationList.table.replyStatus.title')"
        prop="replyStatus"
      >
        <BaseElSelect
          v-model="formData.replyStatus"
          :disabled="isReplyStatusDisabled"
          :placeholder="$t('common.select.placeholder')"
        >
          <BaseElSelectOption
            v-for="option in availableReplyStatusOptions"
            :key="option.value"
            :label="$t(option.name)"
            :value="option.value"
          />
        </BaseElSelect>
      </BaseElFormItem>

      <!-- 付款警告 -->
      <BaseElFormItem v-if="showPaymentWarning">
        <span class="warning">{{ $t('editReservationStatusDialog.paymentWarning.title') }}</span>
      </BaseElFormItem>

      <!-- 地址 -->
      <BaseElFormItem v-if="useMemberAddress" prop="address">
        <AddressInput
          :data="get(data, 'AppointmentOrder.userAddress')"
          :city.sync="formData.address.city"
          :area.sync="formData.address.area"
          :detail.sync="formData.address.detail"
        />
      </BaseElFormItem>

      <!-- 店家備註 -->
      <BaseElFormItem :label="$t('editReservationStatusDialog.remark.title')" prop="remark">
        <BaseElInput
          v-model="formData.remark"
          type="textarea"
          :autosize="{ minRows: 8, maxRows: 8 }"
          :placeholder="$t('common.input.placeholder')"
          maxlength="1000"
          show-word-limit
        />
      </BaseElFormItem>
    </BaseElForm>
  </BaseDialog>
</template>

<script>
import { reactive, computed, onMounted } from 'vue'
import AddressInput from '@/components/Input/AddressInput'
import {
  UpdateReservationOrderStatus,
  UpdateReservationOrderInfo,
  UpdateReservationOrderReplyStatus,
} from '@/api/reservation'
import { statusTranslate } from '@/utils/helper'
import { get } from 'lodash'
import { formatDate, addDays } from '@/utils/date'
import { replyStatusConfig } from '@/config/reservation'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import useDirty from '@/use/useDirty'
import { useQueryClient } from '@tanstack/vue-query'
import { clearAvailabilityQueries } from '@/use/useAvailabilityQuery'

export default {
  name: 'EditReservationDialog',
  components: {
    AddressInput,
    BaseDialog,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    // ========== Reactive Data ==========
    const formData = reactive({
      status: '',
      remark: '',
      replyStatus: null,
      address: {
        city: '',
        area: '',
        detail: '',
      },
    })

    // ========== Composables ==========
    const { shopId } = useShop()
    const queryClient = useQueryClient()
    const { checkAction } = usePermissions()
    const { dirtyFields, startWatch } = useDirty(formData)

    // ========== Computed - 現有邏輯 ==========
    const isCancel = computed(() => {
      const status = get(props.data, 'AppointmentOrder.status', '')
      return status === 'cancel'
    })

    const isAbsence = computed(() => {
      const status = get(props.data, 'AppointmentOrder.status', '')
      return status === 'absence'
    })

    const useMemberAddress = computed(() => {
      return checkAction('adminView.member.findUserInfoMoreAddress')
    })

    const isPubAptAndCardSecret = computed(() => {
      return (
        get(props.data, 'AppointmentOrder.origin') === 'pubApt' &&
        get(props.data, 'AppointmentOrder.AppointmentVisitorPreOrder.paymentConditionType') === 'cardSecret'
      )
    })

    const showPaymentWarning = computed(() => {
      const start = get(props.data, 'AppointmentOrder.start')
      const lastPaymentDate = addDays(start, 7)
      return (
        formatDate(new Date()) < lastPaymentDate &&
        isPubAptAndCardSecret.value &&
        !isCancel.value &&
        !isAbsence.value
      )
    })

    // ========== Computed - 訂單回覆狀態邏輯 ==========

    // 當前訂單回覆狀態
    const currentReplyStatus = computed(() => {
      return get(props.data, 'AppointmentOrder.replyStatus')
    })

    // 是否顯示訂單回覆狀態欄位（當 replyStatus 為 null 時不顯示）
    const showReplyStatusField = computed(() => {
      return currentReplyStatus.value !== null
    })

    // 訂單回覆狀態是否禁用（已回覆狀態不可修改）
    const isReplyStatusDisabled = computed(() => {
      return currentReplyStatus.value === 'replied'
    })

    // 根據當前狀態計算可選擇的選項
    const availableReplyStatusOptions = computed(() => {
      const current = currentReplyStatus.value

      // 狀態轉換映射表
      const transitionMap = {
        pending: [replyStatusConfig.pending, replyStatusConfig.replied, replyStatusConfig.expired],
        expired: [replyStatusConfig.expired, replyStatusConfig.replied],
        no_reply_needed: [replyStatusConfig.no_reply_needed, replyStatusConfig.replied],
        replied: [replyStatusConfig.replied], // disabled 狀態，顯示當前值
      }

      return transitionMap[current] || []
    })

    // ========== Methods ==========

    const updateOrderStatus = async () => {
      if (isCancel.value) return ['ok', null]
      if (!dirtyFields('status')) return ['ok', null]

      const [, err] = await UpdateReservationOrderStatus({
        shopId: shopId.value,
        id: get(props.data, 'AppointmentOrder.id', undefined),
        status: formData.status,
      })

      if (err) return [null, err]
      return ['ok', null]
    }

    const updateOrderInfo = async () => {
      if (!dirtyFields('remark') && !dirtyFields('address')) {
        return ['ok', null]
      }
      const { city, area, detail } = formData.address
      let userAddress
      if (useMemberAddress.value) userAddress = `${city}${area}${detail}`

      const payload = {
        shopId: shopId.value,
        id: get(props.data, 'AppointmentOrder.id', undefined),
        adminComment: formData.remark,
        userAddress: userAddress || undefined,
      }

      const [, err] = await UpdateReservationOrderInfo(payload)
      if (err) return [null, err]
      return ['ok', null]
    }

    const updateOrderReplyStatus = async () => {
      // 如果回覆狀態沒有變更，跳過更新
      if (!dirtyFields('replyStatus')) {
        return ['ok', null]
      }

      const payload = {
        shopId: shopId.value,
        id: get(props.data, 'AppointmentOrder.id', undefined),
        replyStatus: formData.replyStatus,
      }

      const [, err] = await UpdateReservationOrderReplyStatus(payload)
      if (err) return [null, err]
      return ['ok', null]
    }

    const updateOrder = async () => {
      const [, statusUpdateErr] = await updateOrderStatus()
      if (statusUpdateErr) {
        window.$message.error(statusUpdateErr || statusUpdateErr.msg)
        return
      }

      const [, infoUpdateErr] = await updateOrderInfo()
      if (infoUpdateErr) {
        window.$message.error(infoUpdateErr || infoUpdateErr.msg)
        return
      }

      const [, replyStatusUpdateErr] = await updateOrderReplyStatus()
      if (replyStatusUpdateErr) {
        window.$message.error(replyStatusUpdateErr || replyStatusUpdateErr.msg)
        return
      }

      window.$message.success('更新成功')
      clearAvailabilityQueries(queryClient)
      emit('refresh')
      emit('close')
    }

    // ========== Lifecycle ==========
    onMounted(() => {
      formData.status = get(props.data, 'AppointmentOrder.status', '')
      formData.remark = get(props.data, 'AppointmentOrder.adminComment', '')
      formData.replyStatus = currentReplyStatus.value
      startWatch()
    })

    // ========== Return ==========
    return {
      // Data
      formData,

      // Computed
      isCancel,
      isAbsence,
      useMemberAddress,
      isPubAptAndCardSecret,
      showPaymentWarning,

      // 訂單回覆狀態相關
      currentReplyStatus,
      showReplyStatusField,
      isReplyStatusDisabled,
      availableReplyStatusOptions,

      // Methods
      checkAction,
      updateOrder,

      // Utils
      get,
      statusTranslate,
    }
  },
}
</script>

<style lang="postcss" scoped>
.warning {
  @apply text-red-500 leading-[23.17px] inline-flex max-w-[556px]
}
</style>
